/**
 * Version check — cache busting (S13 + S14).
 *
 * Au mount de l'app, fetch /version.json (jamais cached côté serveur
 * grâce aux meta no-store + au query-string `?t=` ci-dessous).
 * Compare avec localStorage["qorum.version"]. Si différent → store +
 * reload pour forcer la prise en compte du nouveau bundle.
 *
 * S14 : re-check toutes les 5 minutes (utilisateurs qui laissent
 * l'app ouverte longtemps) + unregister des Service Workers obsolètes
 * en cas de mismatch (au cas où Vite/PWA en aurait posé un).
 */

const STORAGE_KEY = "qorum.version";
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

export interface VersionPayload {
  version: string;
  builtAt: string;
}

export async function fetchVersion(
  fetchImpl: typeof fetch = fetch,
): Promise<VersionPayload | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as VersionPayload;
  } catch {
    return null;
  }
}

async function unregisterServiceWorkers(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch {
    // best effort
  }
}

export interface CheckDeps {
  storage?: Pick<Storage, "getItem" | "setItem">;
  reload?: () => void;
  fetchImpl?: typeof fetch;
  unregisterSW?: () => Promise<void>;
}

/**
 * Returns true if a reload was triggered.
 */
export async function checkVersionAndReload(
  deps: CheckDeps = {},
): Promise<boolean> {
  const storage =
    deps.storage ??
    (typeof window !== "undefined" ? window.localStorage : undefined);
  const reload =
    deps.reload ??
    (typeof window !== "undefined"
      ? () => window.location.reload()
      : () => {});
  if (!storage) return false;

  const payload = await fetchVersion(deps.fetchImpl);
  if (!payload || !payload.version || payload.version === "dev") return false;

  const stored = storage.getItem(STORAGE_KEY);
  if (stored === payload.version) return false;

  storage.setItem(STORAGE_KEY, payload.version);

  // Bootstrap (no previous value) : ne reload pas, on est déjà sur la
  // bonne version. Reload uniquement sur vrai mismatch.
  if (stored === null) return false;

  // Vrai mismatch → kill SW obsolète puis reload.
  await (deps.unregisterSW ?? unregisterServiceWorkers)();
  reload();
  return true;
}

/**
 * S14 : démarre un polling toutes les 5 minutes. Retourne un cleanup.
 * Le premier check est immédiat.
 */
export function startVersionPolling(
  deps: CheckDeps & { intervalMs?: number } = {},
): () => void {
  const interval = deps.intervalMs ?? VERSION_POLL_INTERVAL_MS;
  void checkVersionAndReload(deps);
  const id = setInterval(() => {
    void checkVersionAndReload(deps);
  }, interval);
  return () => clearInterval(id);
}
